/**
 * useDashboardSave — debounced autosave for the dashboard editor host.
 *
 * Clones the STRUCTURE of the report host's autosave block
 * (apps/web/src/routes/_authenticated/reports/$reportId.edit.tsx):
 *
 *   - a monotonic `version` counter is debounced ~2s (trailing) via useDebounce;
 *   - the editor is "dirty" while `version > lastSavedVersion`;
 *   - each save aborts the previous in-flight request (AbortController) so a fast
 *     edit stream collapses to the latest write;
 *   - save payload + widgets are read from refs (no stale closures), so the
 *     `performSave` callback stays stable across edits;
 *   - on success the cache is mark-clean'd up to the version that was persisted
 *     (edits made mid-save bump it higher → another autosave).
 *
 * The single report seam swapped here: persistence targets the dashboards CRUD
 * (`PUT /api/dashboards/:id { name, canvasLayout: toCanvasLayout(widgets) }`)
 * instead of the reports PUT. A dashboard always has an id (it is created before
 * the editor opens), so there is no create-on-first-save branch.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiPut } from "../lib/apiClient";
import { toCanvasLayout } from "../lib/dashboardLayout";
import { useDebounce } from "./useDebounce";
import type { WidgetConfig } from "../lib/templateUtils";
import type { SaveStatus } from "../components/reports/EditorToolbar";

export interface UseDashboardSaveArgs {
  /** The dashboard id to PUT to. */
  dashboardId: string;
  /** Current dashboard name (saved alongside the canvas). */
  name: string;
  /** Current canvas widgets (the single flat page). */
  widgets: readonly WidgetConfig[];
  /** Publish state — "draft" | "ready" (persisted alongside the canvas). */
  status: string;
  /** Primary range start (ISO "YYYY-MM-DD"), or null when no range is set. */
  dateRangeStart: string | null;
  /** Primary range end (ISO "YYYY-MM-DD"), or null when no range is set. */
  dateRangeEnd: string | null;
  /** Preset key (e.g. "last-30" or "custom"), or null when no range is set. */
  dateRangePreset: string | null;
  /** Monotonic edit counter — bump drives the debounced autosave. */
  version: number;
  /**
   * Gate that must be true before any write fires (mirrors the report host's
   * `hasHydratedRef` clobber-guard): never autosave the empty canvas over a
   * real saved row before the load query has hydrated.
   */
  enabled?: boolean;
}

export interface UseDashboardSaveResult {
  /** "saved" | "saving" | "unsaved" — drives the status pill. */
  saveStatus: SaveStatus;
  /** True while the edit version is past the last persisted version. */
  isDirty: boolean;
  /** Imperative immediate save (Cmd/Ctrl+S, exit flush). */
  save: () => void;
}

export function useDashboardSave({
  dashboardId,
  name,
  widgets,
  status,
  dateRangeStart,
  dateRangeEnd,
  dateRangePreset,
  version,
  enabled = true,
}: UseDashboardSaveArgs): UseDashboardSaveResult {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [lastSavedVersion, setLastSavedVersion] = useState(0);

  // ── Fold the non-canvas metadata into the edit counter ──────────────────
  // The widget `version` only advances on canvas edits. Status + the date-range
  // triple are NOT in that counter, so we bump a sibling `metaVersion` whenever
  // any of them changes and add it to `version`. The combined `effectiveVersion`
  // then drives the SAME dirty edge + debounce that a widget edit does, so
  // publishing or changing the range persists exactly like a layout change.
  const [metaVersion, setMetaVersion] = useState(0);
  const prevMetaRef = useRef({
    status,
    dateRangeStart,
    dateRangeEnd,
    dateRangePreset,
  });
  useEffect(() => {
    const prev = prevMetaRef.current;
    if (
      prev.status !== status ||
      prev.dateRangeStart !== dateRangeStart ||
      prev.dateRangeEnd !== dateRangeEnd ||
      prev.dateRangePreset !== dateRangePreset
    ) {
      prevMetaRef.current = {
        status,
        dateRangeStart,
        dateRangeEnd,
        dateRangePreset,
      };
      setMetaVersion((v) => v + 1);
    }
  }, [status, dateRangeStart, dateRangeEnd, dateRangePreset]);

  const effectiveVersion = version + metaVersion;
  const isDirty = effectiveVersion > lastSavedVersion;

  // ── Refs for values read inside the (stable) save callback ──────────────
  const versionRef = useRef(effectiveVersion);
  versionRef.current = effectiveVersion;

  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;

  const nameRef = useRef(name);
  nameRef.current = name;

  const statusRef = useRef(status);
  statusRef.current = status;

  const dateRangeStartRef = useRef(dateRangeStart);
  dateRangeStartRef.current = dateRangeStart;

  const dateRangeEndRef = useRef(dateRangeEnd);
  dateRangeEndRef.current = dateRangeEnd;

  const dateRangePresetRef = useRef(dateRangePreset);
  dateRangePresetRef.current = dateRangePreset;

  const dashboardIdRef = useRef(dashboardId);
  dashboardIdRef.current = dashboardId;

  // Abort the previous in-flight save before starting a new one.
  const saveAbortRef = useRef<AbortController | null>(null);

  const performSave = useCallback(async () => {
    const id = dashboardIdRef.current;
    if (!id) return;

    saveAbortRef.current?.abort();
    const controller = new AbortController();
    saveAbortRef.current = controller;

    // Snapshot the version being persisted so success can mark clean up to it.
    const versionAtSave = versionRef.current;

    setSaveStatus("saving");
    try {
      const payload = {
        name: nameRef.current,
        canvasLayout: toCanvasLayout(widgetsRef.current),
        status: statusRef.current,
        dateRangeStart: dateRangeStartRef.current,
        dateRangeEnd: dateRangeEndRef.current,
        dateRangePreset: dateRangePresetRef.current,
      };
      const res = await apiPut(`/api/dashboards/${id}`, payload, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (res.ok) {
        setLastSavedVersion(versionAtSave);
        setSaveStatus("saved");
      } else {
        setSaveStatus("unsaved");
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setSaveStatus("unsaved");
    }
  }, []);

  // Trailing ~2s debounce on the edit counter (the report host's cadence).
  const debouncedVersion = useDebounce(effectiveVersion, 2000);

  useEffect(() => {
    if (!enabled || !isDirty) return;
    if (debouncedVersion === 0) return;
    void performSave();
  }, [debouncedVersion, isDirty, enabled, performSave]);

  // Reflect the dirty edge immediately (before the debounce settles).
  useEffect(() => {
    if (isDirty) setSaveStatus("unsaved");
  }, [isDirty]);

  return { saveStatus, isDirty, save: performSave };
}
